import { device } from '@accedo/xdk-core'
import { ID as WORKSTATION } from '@accedo/xdk-device-workstation'
import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../components/button/button'
import { black, getColor, grey300, grey920, white } from '../constants/colors'
import { UniNeueBold, UniNeueRegular } from '../constants/fonts'
import { Images } from '../constants/paths'
import { AppConfig } from '../services/appConfig'
import { deleteUserData } from '../utils/authentication'
import { getText } from '../utils/dictionary'
import { getAsset } from '../utils/image'
import { DevSettingItem, toggleDevSetting } from '../utils/settingsUtils'

export default class ForceCloseWindow extends Lightning.Component {
  private _currentButton
  private _setting: DevSettingItem

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: getColor(grey920),
      collision: true,
      flex: {
        direction: 'column',
      },
      Header: {
        w: (w) => w,
        h: 168,
        rect: true,
        color: getColor(black),
        Logo: {
          x: 73,
          y: 70,
          h: 29,
          w: 246,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.LOGO_LARGE), 256, 29),
        },
      },
      Container: {
        w: (w) => w,
        h: 912,
        y: 80,
        rect: true,
        color: getColor(grey920),
        flex: {
          direction: 'column',
          alignItems: 'center',
        },
        Title: {
          text: {
            text: '',
            fontSize: 48,
            lineHeight: 48,
            fontFace: UniNeueBold,
            textColor: getColor(white),
          },
          flexItem: {
            marginBottom: 16,
          },
        },
        Subtitle: {
          text: {
            text: getText('forceCloseModalSubtitle'),
            fontSize: 40,
            lineHeight: 40,
            fontFace: UniNeueRegular,
            textColor: getColor(grey300),
          },
          flexItem: {
            marginBottom: 48,
          },
        },
        Buttons: {
          flex: {
            direction: 'column',
          },
          OkButton: {
            w: 658,
            type: Button,
            label: getText('okButton'),
            flexItem: {
              marginBottom: 24,
            },
          },
          CancelButton: {
            w: 658,
            type: Button,
            label: getText('cancelButton'),
          },
        },
      },
    }
  }

  get page() {
    return Router.getActivePage()
  }

  get cancelButton() {
    return this.tag('CancelButton') as Button
  }

  get okButton() {
    return this.tag('OkButton') as Button
  }

  get title() {
    return this.tag('Title')
  }

  set setting(v: DevSettingItem) {
    this._setting = v
    this.title.patch({ text: { text: this.getModalTitle(v?.name) } })
  }

  _init() {
    this._currentButton = this.okButton
    this.okButton.zIndex = this.zIndex + 1
    this.cancelButton.zIndex = this.zIndex + 1
  }

  _getFocused() {
    return this._currentButton
  }

  _focus() {
    this.visible = true
  }

  _handleKey() {
    // Prevent modal from trying to focus somewhere else
    return true
  }

  _handleDown() {
    if (this._currentButton === this.okButton) {
      this._currentButton = this.cancelButton
    }
  }

  _handleUp() {
    if (this._currentButton === this.cancelButton) {
      this._currentButton = this.okButton
    }
  }

  _handleEnter() {
    if (this._currentButton === this.okButton) {
      deleteUserData()
      toggleDevSetting(this._setting?.id, !this._setting?.isOn)

      const platform = device.platform

      if (platform === WORKSTATION) {
        AppConfig.shared.restart()
      } else {
        AppConfig.shared.onShutdown()
      }
    } else if (this._currentButton === this.cancelButton) {
      this.refocusPage()
    }
  }

  _handleBack(event) {
    event.preventDefault()

    this.refocusPage()
  }

  _handleCursorHide() {
    // Allows capturing the event when the cursor disappears by being idle
    // triggering this modal's _getFocused() method
    this._refocus()
  }

  getModalTitle(settingName: string) {
    const baseTitle = getText('forceCloseModalTitle')
    const titleWithSetting = baseTitle.replace('{{setting}}', settingName)

    return titleWithSetting
  }

  refocusPage() {
    this.visible = false
    this._currentButton = this.okButton
    ;(this.page as any)._setState('Ready')
    Router.focusPage()
  }

  $updateFocusByHover(button) {
    this._currentButton = button
    this._refocus()
  }

  _handleClick(element) {
    if (element instanceof Button) {
      this._handleEnter()
    }
  }
}
